import { useEffect } from 'react';

const DEFAULT_APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

// Module-level guards so we only prompt/reload once per page lifecycle
let hasPrompted = false;
let hasReloaded = false;

async function performClientReload() {
  if (hasReloaded) return;
  hasReloaded = true;

  if ('serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    } catch (e) {
      // ignore
    }
  }

  if ('caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch (e) {
      // ignore
    }
  }

  const url = new URL(window.location.href);
  url.searchParams.set('_ts', Date.now().toString());
  // Use replace to avoid creating an extra history entry
  window.location.replace(url.toString());
}

export default function useVersionCheck(trigger, appVersion = DEFAULT_APP_VERSION) {
  useEffect(() => {
    if (hasPrompted) return;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const serverVersion = data?.version;
        if (!serverVersion) return;

        if (serverVersion !== appVersion && !cancelled && !hasPrompted) {
          hasPrompted = true;

          const detail = {
            serverVersion,
            // expose a reload function to the UI so the user can confirm reload
            reload: performClientReload,
          };

          try {
            window.dispatchEvent(new CustomEvent('app:version:update-available', { detail }));
          } catch (e) {
            // fallback: if events fail, attempt immediate reload
            performClientReload();
          }
        }
      } catch (e) {
        // network or parse errors - silently ignore
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [trigger, appVersion]);
}
